import { filter } from 'lodash';
import { ErrorBoundary } from '@hl/ui-components';
import React, { Component, PropsWithChildren, Suspense, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import ReactIdContext from '../Global/ReactIdContext';
import { reactWidgetMap } from './WidgetMap';

const checkLoadingStatus = () => {
    const indicator = document.querySelector('#react-widgets-global-loading-indicator');
    if (!indicator) {
        return;
    }
    const count = +(indicator.getAttribute('data-count') ?? 0);
    if (count > 1) {
        indicator.setAttribute('data-count', `${count - 1}`);
    } else {
        indicator.remove();
    }
};

const ClearOriginalOnDisplay: React.FCC<{ container: HTMLElement; reactId: string }> = ({
    container,
    children,
    reactId
}) => {
    const id = `react-widget-${reactId}`;
    useLayoutEffect(() => {
        checkLoadingStatus();
        filter(container.childNodes, (node: ChildNode | HTMLElement) => {
            return !('id' in node && node.id === id);
        }).forEach((node: ChildNode | HTMLElement) => {
            node.remove();
        });

        // intentionally checking this whenever root tries to rerender from component config addition/removals
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return <span id={id}>{children}</span>;
};

const silentMsg = `
removing react from already removed dom element, mostly likely because the existing mvc code removed the rendered react code
if you see an error about
        
    Uncaught DOMException: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.
        
You can safely ignore it
`;

class SilentError extends Component<PropsWithChildren> {
    state = { hasError: false };
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        console.error(silentMsg);
    }
    render() {
        if (this.state.hasError) {
            return null;
        } else {
            return this.props.children;
        }
    }
}

const WidgetPortal: React.FCC<{
    compName: string;
    reactId: string;
    widgetProps: Record<string, unknown>;
    prefetchPromise: Promise<object>;
}> = ({ compName, reactId, widgetProps, prefetchPromise }) => {
    const node = document.getElementById(reactId);
    const Comp = reactWidgetMap[compName];
    if (!node && !Comp) {
        checkLoadingStatus();
    }
    return (
        <SilentError>
            {node && Comp && (
                <>
                    {ReactDOM.createPortal(
                        <ErrorBoundary fallback={null}>
                            <Suspense fallback={null}>
                                <ClearOriginalOnDisplay container={node} reactId={`react-widget-${reactId}`}>
                                    <ReactIdContext.Provider value={reactId}>
                                        <Comp {...widgetProps} prefetchPromise={prefetchPromise} />
                                    </ReactIdContext.Provider>
                                </ClearOriginalOnDisplay>
                            </Suspense>
                        </ErrorBoundary>,
                        node
                    )}
                </>
            )}
        </SilentError>
    );
};

export default WidgetPortal;
